import { Checkbox } from 'antd';
import _isEmpty from 'lodash/isEmpty';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from './usePrevious';
// import { stringify } from 'querystring';
import queryString from 'querystring';
import isEqual from 'lodash/isEqual';

export interface IUseRowSelectionTable {
  dataSource: Record<string, any>[];
  rowKey?: (value: any, index: number, array: any[]) => string;
  page: number;
  totalPageSize: number;
  paramDependencies?: any[];
}

const calcObjLength = (object: Record<string, any>): number =>
  Object.keys(object).reduce(
    (res: number, key) => (res += object[key].length),
    0,
  );

export const useRowSelectionTable = (props: IUseRowSelectionTable) => {
  const { dataSource, rowKey, page, totalPageSize, paramDependencies } = props;

  const [isSelectAllRow, setSelectAllRow] = useState<boolean>(false);
  const [unCheckedList, setUnCheckedList] = useState<Record<number, string[]>>(
    {},
  );

  const [records, setRecords] = useState<any[]>([]);
  const [allPageRecords, setAllPageRecords] = useState<any[]>([]);

  const [checkedListOfAllPage, setCheckedListOfAllPage] = useState<
    Record<number, string[]>
  >({});
  const [selectedCount, setSelectedCount] = useState<number>(0);

  const sourceIds = useMemo(() => {
    return (dataSource || []).map(data => data.id);
  }, [dataSource]);

  //selected Row of fetched Pages (not all pages)
  const checkedListArray = useMemo<string[]>(
    () =>
      Object.keys(checkedListOfAllPage).reduce((res: string[], key) => {
        if (checkedListOfAllPage[Number(key)].length) {
          res = [...res, ...checkedListOfAllPage[Number(key)]];
        }
        return res;
      }, []),
    [checkedListOfAllPage],
  );

  const onChangeRowSelection = useCallback(
    (selectedRowKeys: any[], selectedRows?: any[]) => {
      if (!rowKey) return;

      if (isSelectAllRow) {
        const ids = dataSource.map(rowKey) as string[];
        const x = ids.filter(i => !selectedRowKeys.includes(i));
        setUnCheckedList(s => ({ ...s, ...{ [page]: x } }));
      }
      const newCheckedListOfAllPage = {
        ...checkedListOfAllPage,
        ...{ [page]: selectedRowKeys },
      };
      if (!isEqual(newCheckedListOfAllPage, checkedListOfAllPage)) {
        setCheckedListOfAllPage(newCheckedListOfAllPage);
      }
      if (selectedRows) {
        if (!isEqual(selectedRows, records)) {
          setRecords(selectedRows);
        }
        const oldRecord = allPageRecords.filter(
          record => !sourceIds.includes(record.id),
        );
        const newAllPageRecords = [...new Set([...oldRecord, ...selectedRows])];
        if (!isEqual(newAllPageRecords, allPageRecords)) {
          setAllPageRecords(newAllPageRecords);
        }
      }
    },
    [
      records,
      isSelectAllRow,
      dataSource,
      rowKey,
      page,
      allPageRecords,
      sourceIds,
      checkedListOfAllPage,
    ],
  );

  const checkedListLength = useMemo<number>(
    () => calcObjLength(checkedListOfAllPage),
    [checkedListOfAllPage],
  );

  const toggleSelectAll = useCallback(
    (value: any) => {
      if (!rowKey) return;
      setSelectAllRow(value.target.checked);

      if (!value.target.checked) {
        setRecords([]);
        setAllPageRecords([]);
        setCheckedListOfAllPage({});
      } else {
        const dataSourceIds = dataSource.map(item => item?.id) as string[];
        setCheckedListOfAllPage(s => ({
          ...s,
          ...{
            [page]: dataSourceIds,
          },
        }));
        setRecords(dataSource);
        setAllPageRecords(dataSource);

        setUnCheckedList({});
      }
    },
    [dataSource, page, rowKey],
  );

  const unCheckRowLength = useMemo<number>(
    () => calcObjLength(unCheckedList),
    [unCheckedList],
  );

  const columnTitle = useMemo<ReactNode>(() => {
    let indeterminate = false;
    let checked = false;

    if (rowKey) {
      if (isSelectAllRow) {
        if (!unCheckRowLength) {
          checked = true;
        } else if (unCheckRowLength !== totalPageSize) {
          indeterminate = true;
        }
      } else {
        indeterminate = !!checkedListLength;
      }
    }

    return (
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={toggleSelectAll}
      />
    );
  }, [
    checkedListLength,
    isSelectAllRow,
    rowKey,
    toggleSelectAll,
    totalPageSize,
    unCheckRowLength,
  ]);

  useEffect(() => {
    if (isSelectAllRow) {
      setCheckedListOfAllPage(s => {
        const newCheckedListOfAllPage = {
          ...s,
          ...{
            [page]: dataSource.reduce((acc: string[], dataSourceItem: any) => {
              if (!unCheckedList[page]?.includes(dataSourceItem?.id)) {
                acc.push(dataSourceItem?.id);
              }
              return acc;
            }, []),
          },
        };
        if (!isEqual(newCheckedListOfAllPage, s)) {
          return newCheckedListOfAllPage;
        }
        return s;
      });

      const updatedDataSourceByPage = dataSource.reduce(
        (acc: any[], dataSourceItem: any) => {
          if (!unCheckedList[page]?.includes(dataSourceItem?.id)) {
            acc.push(dataSourceItem);
          }
          return acc;
        },
        [],
      );
      setRecords(updatedDataSourceByPage);
      setAllPageRecords(updatedDataSourceByPage);
    }
  }, [
    dataSource,
    isSelectAllRow,
    page,
    setCheckedListOfAllPage,
    unCheckedList,
  ]);

  useEffect(() => {
    if (isSelectAllRow) {
      if (unCheckRowLength) {
        setSelectedCount(totalPageSize - unCheckRowLength);
        return;
      }
      setSelectedCount(totalPageSize);
      return;
    }
    setSelectedCount(checkedListLength);
  }, [
    unCheckRowLength,
    isSelectAllRow,
    totalPageSize,
    unCheckedList,
    checkedListLength,
    setSelectedCount,
  ]);

  useEffect(() => {
    if (selectedCount !== 0 && selectedCount === totalPageSize) {
      setSelectAllRow(true);
    }
  }, [selectedCount, totalPageSize]);

  useEffect(() => {
    if (_isEmpty(checkedListOfAllPage)) {
      setSelectAllRow(false);
    }
  }, [checkedListOfAllPage]);

  const queryKey = useMemo<string>(() => {
    if (!paramDependencies || !paramDependencies.length) return '';
    return paramDependencies.reduce((script, param) => {
      if (typeof param === 'object') {
        script += queryString.stringify(param);
      }
      script += param;
      return script;
    }, '');
  }, [paramDependencies]);

  const queryKeyPrevious = usePrevious(queryKey);

  const resetAll = useCallback(() => {
    setUnCheckedList([]);
    setCheckedListOfAllPage({});
    setSelectAllRow(false);
  }, []);

  useEffect(() => {
    if (queryKeyPrevious && queryKeyPrevious !== queryKey) {
      resetAll();
    }
  }, [queryKey, queryKeyPrevious, resetAll]);

  const unCheckedListArr = useMemo<string[]>(
    () =>
      Object.keys(unCheckedList).reduce((res: string[], key) => {
        res = [...res, ...unCheckedList[Number(key)]];
        return res;
      }, []),
    [unCheckedList],
  );

  return {
    selectedCount,
    checkedListOfAllPage,
    unCheckedList,
    isSelectAllRow,
    onChangeRowSelection,
    columnTitle,
    checkedListArray,
    setCheckedListOfAllPage,
    setSelectAllRow,
    unCheckedListArr,
    records,
    setRecords,
    allPageRecords,
    setAllPageRecords,
  };
};
